import React, { FC, useEffect, useRef, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { PostTemplatePostFragmentFragment } from "@graphqlTypes"

import { isClientSide, oneLineHelpScout, convertStringToKebabCase } from "@utils"

import HubspotForm from "@components/HubspotForm/HubspotForm"
import WistiaWithPreview from "@components/WistiaWithPreview/WistiaWithPreview"

import PostHeroWebinarHosts from "./components/PostHeroWebinarHosts/PostHeroWebinarHosts"

import "./PostHeroWebinar.scss"

type PostHeroWebinarProps = {
  authors: PostTemplatePostFragmentFragment["authors"],
  description: PostTemplatePostFragmentFragment["description"],
  hubspotFormData: PostTemplatePostFragmentFragment["hubspotFormData"],
  series: PostTemplatePostFragmentFragment["series"],
  title: PostTemplatePostFragmentFragment["title"],
  wistiaId: PostTemplatePostFragmentFragment["wistiaId"],
  wistiaPreviewImage:
  PostTemplatePostFragmentFragment["wistiaPreviewImage"] |
  PostTemplatePostFragmentFragment["image"] |
  PostTemplatePostFragmentFragment["thumbnailImage"],
}

const PostHeroWebinar: FC<PostHeroWebinarProps> = ({
  authors,
  description,
  hubspotFormData,
  series,
  title,
  wistiaId,
  wistiaPreviewImage,
}) => {
  const { defaultWebinarHubspotFormData } = useStaticQuery(graphql`
    query PostHeroWebinarQuery {
      defaultWebinarHubspotFormData: hubspotForm(alternative_id:{eq:"43aa9c58-bb58-401c-980d-8d1cdaa0f397"}) {
        ...HubspotFormFragment
      }
    }
  `)

  const [isRegistered, setIsRegistered] = useState<boolean>(false)
  const webinarFormRef = useRef<HTMLDivElement | null>(null)
  const customSuccessMessage = 'Thank you for registering! A confirmation email has been sent! If you do not see the email, please check your spam folder.'
  const hubspotFormDataToUse = hubspotFormData || defaultWebinarHubspotFormData

  const IS_REGISTERED_LS_KEY = `hs--webinar--is-registered--${convertStringToKebabCase(title || "")}`

  const onFormSubmitted = () => {
    setIsRegistered(true)
    window.localStorage.setItem(IS_REGISTERED_LS_KEY, "true")
  }

  useEffect(() => {
    if (!isClientSide()) return

    const cachedIsRegistered = window.localStorage.getItem(IS_REGISTERED_LS_KEY)
    setIsRegistered(!!cachedIsRegistered)

    // Add event listener for gated hubspot form's onFormReady event - v2 API
    window.addEventListener('message', event => {
      if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
        // Re-order form DOM
        if (webinarFormRef.current) {
          // Add non breaking space to Help Scout
          const consentField: HTMLParagraphElement | null = webinarFormRef.current.querySelector('.hs-form-booleancheckbox-display span p')
          if (consentField) {
            const _innerText = consentField.innerText
            const _newText = _innerText.replace("Help Scout", `Help${String.fromCharCode(160)}Scout`)
            consentField.innerText = _newText
          }

        }
      }
    })
  }, [])

  return (
    <div className="PostHeroWebinar">
      <div className="container">
        <div className="grid">
          <div className="column column--3-11-S column--2-12-M column--1-7-L">
            <div className="PostHeroWebinar--content">
              <div className="pill font-family--mono">
                {(series && series.length > 0 && series[0]?.title) || `Live Event`}
              </div>
              <h1 className="font--display-M" dangerouslySetInnerHTML={{
                __html: oneLineHelpScout(title || "")
              }} />
              <p>{description?.description}</p>
            </div>
          </div>
          <div className="column mobile">
            <PostHeroWebinarHosts hosts={authors} />
          </div>
          <div className="webinar-form column column--7-13-L">
            <div
              className={`PostHeroWebinar--form ${wistiaId ? 'has-video' : ''}`}
              ref={webinarFormRef}
            >
              {wistiaId ? (
                <WistiaWithPreview
                  id={wistiaId}
                  previewImageData={wistiaPreviewImage && wistiaPreviewImage.imgixImage}
                  title={title || "Video Preview"}
                />
              ) : (
                <div className={`PostHeroWebinar--form-frame ${isRegistered ? "is-registered" : ""}`}>
                  {isRegistered ? (
                    <>
                      <img
                        className="checkmark-image"
                        src="/images/components/PostHeroWebinar/PostHeroWebinar--checkmark.svg"
                      />
                      <h6>You are registered</h6>
                      <p>Check your email for the Zoom link and calendar confirmation.</p>
                    </>
                  ) : (
                    <>
                      <h5 className="margin-bottom--base-1-5">Register Now</h5>
                      <HubspotForm
                        {...hubspotFormDataToUse}
                        customSuccessMessage={customSuccessMessage}
                        onSubmit={onFormSubmitted}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="column desktop">
            <PostHeroWebinarHosts hosts={authors} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PostHeroWebinar
